/**
 * Hook para gerenciar disponibilidade e horários
 */

import { useState, useEffect } from "react";
import type { TimeSlot, AvailabilityMeta } from "../types";
import {
  getPublicAvailability,
  getPublicWeeklyDays,
  getPublicBlackoutDates,
  getPublicProviders,
  getPublicUnitClosures,
  type PublicUnitClosure,
} from "../../../services/public";
import { generateAvailableDates, todayYMD } from "../utils";
import { getScheduleSuggestionCandidates } from "../utils/scheduleSuggestion";

const PUBLIC_CLOSURE_RANGE_DAYS = 120;
/** Quantas datas seguintes tentamos antes de desistir de sugerir uma data com horário livre. */
const MAX_SUGGESTED_DATE_PROBES = 8;

interface UseAvailabilityProps {
  slug: string | null;
  date: string;
  selectedServiceId?: string;
  selectedServiceIds?: string[];
  selectedProviderId?: string;
  multiEnabled?: boolean;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function normalizePublicUnitClosures(
  closures: PublicUnitClosure[] | null | undefined,
  today: string = todayYMD()
): PublicUnitClosure[] {
  return (Array.isArray(closures) ? closures : [])
    .filter((item) => item?.date && item?.publicMessage && item.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function useAvailability({
  slug,
  date,
  selectedServiceId,
  selectedServiceIds = [],
  selectedProviderId,
  multiEnabled,
}: UseAvailabilityProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [slots, setSlots] = useState<string[]>([]);
  const [fitSlots, setFitSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [weeklyDays, setWeeklyDays] = useState<number[]>([]);
  const [availabilityMeta, setAvailabilityMeta] = useState<AvailabilityMeta | null>(null);
  const [publicUnitClosures, setPublicUnitClosures] = useState<PublicUnitClosure[]>([]);

  const [providerSlots, setProviderSlots] = useState<Record<string, string[]>>({});
  const [loadingProviderSlots, setLoadingProviderSlots] = useState(false);

  const [suggestedDate, setSuggestedDate] = useState<string | null>(null);
  const [loadingSuggestedDate, setLoadingSuggestedDate] = useState(false);

  // Carregar dias da semana disponíveis
  useEffect(() => {
    if (!slug) return;

    getPublicWeeklyDays(slug, selectedServiceId || undefined)
      .then(async (list) => {
        setWeeklyDays(list || []);
        const dates = generateAvailableDates(list || []);
        const today = todayYMD();
        const closureRangeFrom = today;
        const closureRangeTo = formatDateOnly(
          addDays(new Date(`${today}T00:00:00`), PUBLIC_CLOSURE_RANGE_DAYS)
        );
        const closuresPromise = getPublicUnitClosures(
          slug,
          closureRangeFrom,
          closureRangeTo
        );
        if (!slug || dates.length === 0) {
          setAvailableDates(dates);
          try {
            const closures = await closuresPromise;
            setPublicUnitClosures(normalizePublicUnitClosures(closures));
          } catch {
            setPublicUnitClosures([]);
          }
          return;
        }
        try {
          const blocked = await getPublicBlackoutDates(
            slug,
            dates[0],
            dates[dates.length - 1]
          );
          const closures = await closuresPromise;
          setPublicUnitClosures(normalizePublicUnitClosures(closures));
          const blockedSet = new Set(blocked || []);
          setAvailableDates(dates.filter((d) => !blockedSet.has(d)));
        } catch {
          setAvailableDates(dates);
          setPublicUnitClosures([]);
        }
      })
      .catch(() => {
        setWeeklyDays([]);
        setAvailableDates([]);
        setPublicUnitClosures([]);
      });
  }, [slug, selectedServiceId]);

  // Carregar slots de horário
  useEffect(() => {
    if (!slug || !date) {
      setSlots([]);
      setFitSlots([]);
      return;
    }

    setLoadingSlots(true);
    setAvailabilityMeta(null);

    const serviceId =
      multiEnabled && selectedServiceIds.length > 0
        ? undefined
        : selectedServiceId || undefined;
    const providerId = selectedProviderId || undefined;
    const serviceIds =
      multiEnabled && selectedServiceIds.length > 0 ? selectedServiceIds : undefined;

    getPublicAvailability(slug, date, serviceId, providerId, serviceIds)
      .then((data) => {
        setSlots(data.slots || []);
        setFitSlots(data.fitSlots || []);
        // Se recebemos slots, mas a lista de datas disponíveis está vazia (ex.: fallback do estabelecimento),
        // garantimos que a data atual conste em availableDates para a UI não ficar sem datas navegáveis
        setAvailableDates((prev) => {
          if ((data.slots || []).length > 0 && (!prev || prev.length === 0)) {
            if (!prev.includes(date)) return [date];
          }
          return prev;
        });
        // Monta meta a partir dos campos do response
        if (data.reasonCode || data.reason || data.details) {
          setAvailabilityMeta({
            reasonCode: data.reasonCode,
            reason: data.reason,
            details: data.details,
          });
        } else {
          setAvailabilityMeta(null);
        }
      })
      .catch(() => {
        setSlots([]);
        setFitSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [
    slug,
    date,
    selectedServiceId,
    selectedProviderId,
    multiEnabled,
    selectedServiceIds,
    refreshKey,
  ]);

  // Data selecionada sem horário: sonda as próximas datas abertas até achar
  // uma com vaga, para não deixar o cliente parado num dia sem nada. `date`
  // continua sendo escolha de quem está fora deste hook — aqui só sugerimos.
  useEffect(() => {
    setSuggestedDate(null);

    if (!slug || loadingSlots || slots.length > 0 || availableDates.length === 0) {
      setLoadingSuggestedDate(false);
      return;
    }

    const candidates = getScheduleSuggestionCandidates(
      date,
      availableDates,
      MAX_SUGGESTED_DATE_PROBES
    );

    if (candidates.length === 0) {
      setLoadingSuggestedDate(false);
      return;
    }

    let cancelled = false;
    setLoadingSuggestedDate(true);

    const serviceId =
      multiEnabled && selectedServiceIds.length > 0
        ? undefined
        : selectedServiceId || undefined;
    const providerId = selectedProviderId || undefined;
    const serviceIds =
      multiEnabled && selectedServiceIds.length > 0 ? selectedServiceIds : undefined;

    (async () => {
      for (const candidateDate of candidates) {
        if (cancelled) return;
        try {
          const data = await getPublicAvailability(
            slug,
            candidateDate,
            serviceId,
            providerId,
            serviceIds
          );
          if (cancelled) return;
          if ((data.slots || []).length > 0) {
            setSuggestedDate(candidateDate);
            setLoadingSuggestedDate(false);
            return;
          }
        } catch {
          // segue tentando a próxima data candidata
        }
      }
      if (!cancelled) setLoadingSuggestedDate(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    slug,
    date,
    slots.length,
    loadingSlots,
    availableDates,
    selectedServiceId,
    selectedProviderId,
    multiEnabled,
    selectedServiceIds,
  ]);

  // Carregar horários por profissional (para exibição em grid)
  useEffect(() => {
    if (!slug || !date) {
      setProviderSlots({});
      return;
    }

    setLoadingProviderSlots(true);

    getPublicProviders(slug, selectedServiceId || undefined, date)
      .then(async (providersList) => {
        if (!providersList || providersList.length === 0) {
          setProviderSlots({});
          setLoadingProviderSlots(false);
          return;
        }

        const ps: Record<string, string[]> = {};

        const promises = providersList.map((p) =>
          getPublicAvailability(
            slug,
            date,
            multiEnabled && selectedServiceIds.length > 0
              ? undefined
              : selectedServiceId || undefined,
            p.id,
            multiEnabled && selectedServiceIds.length > 0 ? selectedServiceIds : undefined
          )
            .then((s) => ({ id: p.id, slots: s.slots }))
            .catch(() => ({ id: p.id, slots: [] }))
        );

        const list = await Promise.all(promises);
        list.forEach((it) => (ps[it.id] = it.slots));
        setProviderSlots(ps);
        setLoadingProviderSlots(false);
      })
      .catch(() => {
        setProviderSlots({});
        setLoadingProviderSlots(false);
      });
  }, [slug, date, selectedServiceId, multiEnabled, selectedServiceIds, refreshKey]);

  const refetch = () => setRefreshKey((k) => k + 1);

  return {
    slots,
    fitSlots,
    loadingSlots,
    availableDates,
    weeklyDays,
    availabilityMeta,
    publicUnitClosure: publicUnitClosures.find((item) => item.date === date) || null,
    publicUnitClosures,
    providerSlots,
    loadingProviderSlots,
    suggestedDate,
    loadingSuggestedDate,
    refetch,
  };
}
